import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  DimeAvatar,
  DimeAvatarFallback,
  DimeButton,
  DimeAutocomplete,
  DimeChip,
  DimeTabs,
  DimeTabsContent,
  DimeTabsList,
  DimeTabsTrigger,
} from '@nudool/design-system';
import { MAX_SEARCH_TERM_LENGTH } from '@nudool/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { ActivityTabPlaceholder } from '../../components/ActivityTabPlaceholder.js';
import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { useDeniedTooltip } from '../../lib/access/index.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import {
  GET_SYS_USER,
  GET_USERS,
  type GetSysUserQueryResult,
  type GetSysUserQueryVariables,
} from '../../modules/iam/operations/index.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { UserActiveStatusControl } from '../../modules/users/user-active-status-control.js';
import { UserDetailAccessTab } from '../../modules/users/user-detail-access-tab.js';
import { UserDetailGeneralTab } from '../../modules/users/user-detail-general-tab.js';
import { UserDetailOverviewTab } from '../../modules/users/user-detail-overview-tab.js';
import { UserEditForm } from '../../modules/users/user-edit-form.js';

export const Route = createFileRoute('/users/$userId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  validateSearch: (search: Record<string, unknown>) => ({
    edit: search.edit === true || search.edit === 'true',
  }),
  component: UserDetailPage,
});

type UserOption = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
};

// eslint-disable-next-line react-refresh/only-export-components
function UserDetailPage() {
  const { userId } = Route.useParams();
  const { edit: startEditing } = Route.useSearch();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('user:edit');
  const deniedTooltip = useDeniedTooltip();
  const [isEditing, setIsEditing] = useState(() => startEditing);
  const [searchInput, setSearchInput] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, loading, refetch } = useQuery<GetSysUserQueryResult, GetSysUserQueryVariables>(
    GET_SYS_USER,
    { variables: { id: userId } },
  );

  const [fetchUsers, { data: usersData, loading: usersLoading }] = useLazyQuery(GET_USERS, {
    fetchPolicy: 'network-only',
  });

  useBreadcrumbLabel(
    userId,
    [data?.sysUser?.firstName?.trim(), data?.sysUser?.lastName?.trim()].filter(Boolean).join(' ') ||
      undefined,
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // GET_USERS no longer bakes in the active-only filter, so pass it here.
      fetchUsers({
        variables: {
          first: 20,
          search: searchInput.trim() || undefined,
          where: { activeFlag: { equals: true } },
        },
      });
    }, 300);
    return () => {
      /* v8 ignore next */
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput, fetchUsers]);

  if (loading) return <div>Loading...</div>;

  const user = data?.sysUser;
  if (!user) return <div>User not found</div>;

  const fullName = [user.firstName?.trim(), user.lastName?.trim()].filter(Boolean).join(' ') || '—';
  const initials = [user.firstName?.[0], user.lastName?.[0]].filter(Boolean).join('').toUpperCase();

  const userOptions: UserOption[] = usersData?.sysUsers.edges.map((edge) => edge.node) ?? [];

  const userAutocompleteOptions = userOptions.map((option) => ({
    value: option.id,
    label: [option.firstName?.trim(), option.lastName?.trim()].filter(Boolean).join(' ') || '—',
    description: option.email ?? undefined,
  }));

  const handleUserSelect = (selectedId: string) => {
    /* v8 ignore next */
    if (!selectedId) return;
    setSearchInput('');
    if (selectedId === userId) return;
    navigate({ to: '/users/$userId', params: { userId: selectedId }, search: { edit: false } });
  };

  if (isEditing) {
    return (
      <div>
        <UserEditForm
          user={user}
          onCancel={() => setIsEditing(false)}
          onSaved={() => {
            setIsEditing(false);
            void refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <DimeAvatar>
          <DimeAvatarFallback>{initials || '?'}</DimeAvatarFallback>
        </DimeAvatar>

        <DimeAutocomplete
          key={user.id}
          id="switch-user"
          aria-label="Switch user"
          placeholder={fullName}
          emptyMessage={usersLoading ? 'Searching…' : 'No users found.'}
          options={userAutocompleteOptions}
          value=""
          onValueChange={handleUserSelect}
          onSearchChange={setSearchInput}
          maxLength={MAX_SEARCH_TERM_LENGTH}
          className="w-56"
        />

        <DimeChip variant={user.activeFlag ? 'success' : 'destructive'}>
          {user.activeFlag ? 'Active' : 'Inactive'}
        </DimeChip>

        <div className="ml-auto flex items-center gap-2">
          <DimeButton
            type="button"
            onClick={() => setIsEditing(true)}
            disabled={!canEdit}
            disabledTooltip={deniedTooltip('user:edit')}
          >
            Edit
          </DimeButton>
          <UserActiveStatusControl
            userId={user.id!}
            userName={fullName}
            activeFlag={user.activeFlag}
            onStatusChanged={() => void refetch()}
            disabled={!canEdit}
            disabledTooltip={deniedTooltip('user:edit')}
          />
        </div>
      </div>

      <DimeTabs defaultValue="overview" className="mt-4">
        <DimeTabsList>
          <DimeTabsTrigger value="overview">Overview</DimeTabsTrigger>
          <DimeTabsTrigger value="general">General</DimeTabsTrigger>
          <DimeTabsTrigger value="access">Access</DimeTabsTrigger>
          <DimeTabsTrigger value="activity">Activity</DimeTabsTrigger>
        </DimeTabsList>
        <DimeTabsContent value="overview">
          <UserDetailOverviewTab user={user} />
        </DimeTabsContent>
        <DimeTabsContent value="general">
          <UserDetailGeneralTab user={user} />
        </DimeTabsContent>
        <DimeTabsContent value="access">
          <UserDetailAccessTab user={user} onChanged={() => void refetch()} />
        </DimeTabsContent>
        <DimeTabsContent value="activity">
          <ActivityTabPlaceholder />
        </DimeTabsContent>
      </DimeTabs>
    </div>
  );
}
